import React, { ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useUIDSeed } from 'react-uid';
import { ErrorResponse, ReferralReportVersion, ScanFile } from '../../types';
import { useUpdateVersion } from '../../data/versions';

const messages = defineMessages({
  messageAttachmentButton: {
    defaultMessage: 'Add attachments',
    description:
      'Accessible label for the attachments button in ButtonFileUploader.',
    id: 'components.ButtonFileUploader.messageAttachmentButton',
  },
});

export const VersionUpdateButton = ({
  onSuccess,
  onError,
  url,
  children,
  cssClass = 'btn-default',
  icon,
  setIsLoading,
  disabled = false,
  disabledText = '',
}: {
  icon?: ReactNode;
  cssClass?: string;
  onSuccess: (result: any) => void;
  onError: (error: ErrorResponse) => void;
  onLoad?: () => void;
  action: string;
  disabled?: boolean;
  disabledText?: string;
  url: string;
  setIsLoading: (boolean: boolean) => void;
  children: React.ReactNode;
}) => {
  const seed = useUIDSeed();
  const mutation = useUpdateVersion(url, 'referrals');
  const onDrop = (acceptedFiles: File[]) => {
    setIsLoading(true);
    const keyValueFiles: [string, File][] = [];
    acceptedFiles.forEach((file) => {
      keyValueFiles.push(['files', file]);
    });

    mutation.mutate([...keyValueFiles], {
      onError: (error: ErrorResponse) => {
        setIsLoading(false);
        return onError(error as ErrorResponse);
      },

      onSuccess: (version: ReferralReportVersion) => {
        onSuccess(version);
        setIsLoading(false);
      },
    });
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <button
      type="button"
      {...getRootProps()}
      className={`btn ${cssClass} relative rounded-sm pt-1 pb-1 pr-2 pl-2 flex items-center`}
      disabled={disabled}
      aria-labelledby={seed('message-attachment-button')}
      data-disabled={disabledText}
    >
      {icon && <div className={`mr-2`}>{icon}</div>}
      <input {...getInputProps()} />
      {children ?? <FormattedMessage {...messages.messageAttachmentButton} />}
    </button>
  );
};
